import { useId, useRef, useState, type PointerEvent } from "react";
import { PenLine, RotateCcw } from "lucide-react";

// Store pen coordinates, not image bytes. SVG renders them on screen and in print.
type Point = [number, number];
type Stroke = Point[];
const PREFIX = "signature:v1:";
const MAX_POINTS = 800;

function readStrokes(value: string): Stroke[] {
  if (!value.startsWith(PREFIX)) return [];
  try {
    const parsed: unknown = JSON.parse(value.slice(PREFIX.length));
    if (!Array.isArray(parsed) || !parsed.every(stroke =>
      Array.isArray(stroke) && stroke.every(point =>
        Array.isArray(point) && point.length === 2 &&
        point.every(Number.isFinite) &&
        point[0] >= 0 && point[0] <= 600 && point[1] >= 0 && point[1] <= 200
      )
    )) return [];
    return parsed as Stroke[];
  } catch { return []; }
}

export default function SignaturePad({ label, value, onChange, disabled = false }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const labelId = useId();
  const activePointer = useRef<number | null>(null);
  const working = useRef<Stroke[]>([]);
  const [limitReached, setLimitReached] = useState(false);
  const strokes = readStrokes(value);
  const legacy = value && !value.startsWith(PREFIX);

  const pointAt = (event: PointerEvent<SVGSVGElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    return [
      Math.round(Math.max(0, Math.min(600, (event.clientX - rect.left) / rect.width * 600))),
      Math.round(Math.max(0, Math.min(200, (event.clientY - rect.top) / rect.height * 200))),
    ];
  };
  const publish = () => onChange(PREFIX + JSON.stringify(working.current));
  const start = (event: PointerEvent<SVGSVGElement>) => {
    if (disabled || legacy || activePointer.current !== null || event.button !== 0) return;
    if (strokes.flat().length >= MAX_POINTS) { setLimitReached(true); return; }
    event.preventDefault();
    activePointer.current = event.pointerId;
    event.currentTarget.setPointerCapture(event.pointerId);
    working.current = [...strokes, [pointAt(event)]];
    publish();
  };
  const move = (event: PointerEvent<SVGSVGElement>) => {
    if (disabled || activePointer.current !== event.pointerId) return;
    if (working.current.flat().length >= MAX_POINTS) { setLimitReached(true); return; }
    const point = pointAt(event);
    const stroke = working.current[working.current.length - 1];
    const last = stroke[stroke.length - 1];
    if (Math.hypot(point[0] - last[0], point[1] - last[1]) < 2) return;
    stroke.push(point);
    publish();
  };
  const stop = (event: PointerEvent<SVGSVGElement>) => {
    if (activePointer.current !== event.pointerId) return;
    activePointer.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div className="space-y-2 break-inside-avoid">
      <div className="flex items-center justify-between gap-2">
        <span id={labelId} className="text-sm font-medium text-slate-900 flex items-center gap-2">
          <PenLine className="w-4 h-4 print:hidden" />{label}
        </span>
        {!disabled && <button type="button" className="text-xs text-slate-600 flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100 print:hidden"
          onClick={() => { activePointer.current = null; working.current = []; onChange(""); setLimitReached(false); }}>
          <RotateCcw className="w-3 h-3" /> Clear
        </button>}
      </div>
      <div className="relative overflow-hidden rounded-lg border-2 border-slate-300 bg-white">
        <svg viewBox="0 0 600 200" preserveAspectRatio="none" role="img" aria-labelledby={labelId}
          aria-label={disabled ? "Saved signature" : "Draw your signature with a finger, pen or mouse"}
          className={`block w-full h-40 ${disabled ? "" : "touch-none cursor-crosshair"}`}
          onPointerDown={start} onPointerMove={move} onPointerUp={stop}
          onPointerCancel={stop} onLostPointerCapture={() => { activePointer.current = null; }}>
          <line x1="20" x2="580" y1="170" y2="170" stroke="#cbd5e1" strokeDasharray="5 5" />
          {strokes.map((stroke, index) => stroke.length === 1
            ? <circle key={index} cx={stroke[0][0]} cy={stroke[0][1]} r="1.5" fill="#0f172a" />
            : <polyline key={index} points={stroke.map(point => point.join(",")).join(" ")}
                fill="none" stroke="#0f172a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />)}
          {legacy && <text x="20" y="110" fontSize="25" fill="#0f172a">{value}</text>}
        </svg>
        {!value && <span className="absolute inset-0 flex items-center justify-center text-sm text-slate-400 pointer-events-none print:hidden">
          {disabled ? "No signature" : "Sign here"}
        </span>}
      </div>
      {!disabled && <p className="text-xs text-slate-500 print:hidden">
        {legacy ? "Previously saved typed signature. Clear it to sign by hand." : "Use your finger, stylus or mouse. Save the form to keep your signature."}
      </p>}
      {limitReached && <p role="status" className="text-xs text-amber-700 print:hidden">Signature area is full. Save this signature, or clear it to sign again.</p>}
    </div>
  );
}